import React, { useMemo } from 'react';
import { Field, Form, FormikProps } from 'formik';
import ReactQuill from 'react-quill-new';

import Box from '@material-ui/core/Box';
import CircularProgress from '@material-ui/core/CircularProgress';
import FormControl from '@material-ui/core/FormControl';
import IconButton from '@material-ui/core/IconButton';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import CloseIcon from '@material-ui/icons/Close';
import Alert from '@material-ui/lab/Alert';

import { FormikInput } from '../../_core/Formik/formik-input.component';
import { OutlineButton } from '../../_core/_ui/buttons.component';
import { FormHelperText, FormLabel } from '../../_core/_ui/forms.component';
import {
  CommunicationContextType,
  DeliveryOption,
  DELIVERY_OPTION_LABELS,
  IAdHocTokenReport
} from '../../../lib/types/communication.types';
import {
  deliveryOptionRequiresEmail,
  deliveryOptionRequiresSMS
} from '../../../lib/helpers/communication.helpers';
import { useAvailableVariables } from '../../../lib/services/communication.service';
import { idsOfType } from '../AudienceSelector/audience-selector.helpers';
import { IComposePanelFields, ITestSourceContext } from './compose-panel.types';
import { COMPOSE_FORM_ID, DELIVERY_OPTION_ORDER } from './compose-panel.constants';
import { InsertTarget } from './compose-panel.insert';
import { buildFieldTokenMessages } from './compose-panel.validation';
import { ComposeSuccessView } from './compose-success-view.component';
import { ComposeEmailSection } from './compose-email-section.component';
import { ComposeSmsSection } from './compose-sms-section.component';
import { ComposePanelFooter } from './compose-panel-footer.component';
import { PreviewDialog } from './preview-dialog.component';
import { FormBody } from './compose-panel.styles';

interface IComposePanelProps {
  form: FormikProps<IComposePanelFields>;
  // The panel's assessment scope — feeds the variable library's registry query.
  assessmentId: string;
  // Variable-insertion plumbing: the container tracks which content field last
  // held focus, plus the email editor's instance + caret via these three.
  quillRef: React.RefObject<ReactQuill>;
  onFieldFocus: (target: InsertTarget) => void;
  onQuillSelectionCapture: (range: ReactQuill.Range) => void;
  // Inserts a "{key}"/"{key|default}" token at the last-focused content field.
  onInsertVariable: (token: string) => void;
  // Latest settled server token report; drives the in-field invalid-token
  // highlight and the live per-field issue text.
  tokenReport?: IAdHocTokenReport;
  // Session sport-term (e.g. "Ice Time") for the token-issue message copy.
  sessionTerm: string;
  // Distinct normalized addresses that will receive ≥1 message.
  recipientCount: number;
  // Total messages = (player × address). messageCount > recipientCount means
  // some recipients receive multiple messages (one per player they're on).
  messageCount: number;
  unreachableCount: number;
  recipientCountLoading: boolean;
  // Drives the preview modal: onTogglePreview opens it from the footer button
  // and closes it from the modal; showPreview is its open state.
  showPreview: boolean;
  onTogglePreview: () => void;
  // Server-substituted preview region (built by the container), shown in the modal.
  previewSlot: React.ReactNode;
  showDraftRestoredBanner: boolean;
  onDismissDraftBanner: () => void;
  onDiscardDraft: () => void;
  submitError?: string;
  // Messages accepted into the delivery queue (real-send response). Present only
  // after a successful send; switches the panel to the "Queued N messages" view.
  queuedCount?: number;
  onComposeAnother: () => void;
  audienceSlot: React.ReactNode;
  // True while a fresh-from-a-page draft resolves its context; shows a loader.
  loadingPageContext: boolean;
  // Scopes + gates the test-send dialog's "render variables from" source picker.
  testSource: ITestSourceContext;
  // Delivers the composed message to typed-in test recipients; resolves with the
  // number of messages the server reported sent, or rejects with a message.
  onSendTest: (emails: string[], phones: string[], testSourceId?: string) => Promise<number>;
  // Per-company+user localStorage key for the test dialog's last-used recipients.
  testRecipientsStorageKey: string;
}

export const ComposePanel: React.FC<IComposePanelProps> = ({
  form,
  assessmentId,
  quillRef,
  onFieldFocus,
  onQuillSelectionCapture,
  onInsertVariable,
  tokenReport,
  sessionTerm,
  recipientCount,
  messageCount,
  unreachableCount,
  recipientCountLoading,
  showPreview,
  onTogglePreview,
  previewSlot,
  showDraftRestoredBanner,
  onDismissDraftBanner,
  onDiscardDraft,
  submitError,
  queuedCount,
  onComposeAnother,
  audienceSlot,
  loadingPageContext,
  testSource,
  onSendTest,
  testRecipientsStorageKey
}) => {
  const { values, isSubmitting, setFieldValue, validateForm, setTouched } = form;

  const showEmailTemplate = useMemo(
    () => deliveryOptionRequiresEmail(values.deliveryOption),
    [values.deliveryOption]
  );
  const showSMSTemplate = useMemo(
    () => deliveryOptionRequiresSMS(values.deliveryOption),
    [values.deliveryOption]
  );
  // Gates the variable library's session-scoped group; the server's effective
  // catalog stays the validation truth — hiding is presentation only.
  const hasSessionContext = useMemo(
    () => idsOfType(values.contexts, CommunicationContextType.ASSESSMENT_SESSION).length > 0,
    [values.contexts]
  );
  // Full variable catalog, fetched only once there are unknowns to classify
  // (cache-first — shares the variable library's query). A catalogued unknown is
  // context-gated; anything else is a spelling error.
  const hasUnknowns = !!(tokenReport && tokenReport.unknown.length > 0);
  const { variables: variableCatalog } = useAvailableVariables(hasUnknowns ? assessmentId : undefined);
  const knownVariableKeys = useMemo(
    () => (variableCatalog.length ? new Set(variableCatalog.map((variable) => variable.key)) : undefined),
    [variableCatalog]
  );
  // Live per-field issue messages, keyed by surface — the same copy the send gate
  // keys off, computed from the settled report as the user types.
  const fieldTokenMessages = useMemo(
    () => buildFieldTokenMessages(
      tokenReport,
      { subject: values.subject, bodyHtml: values.bodyHtml, smsBody: values.smsBody },
      sessionTerm,
      knownVariableKeys
    ),
    [tokenReport, values.subject, values.bodyHtml, values.smsBody, sessionTerm, knownVariableKeys]
  );

  if (typeof queuedCount === 'number') {
    return (
      <ComposeSuccessView queuedCount={queuedCount} onComposeAnother={onComposeAnother} />
    );
  }

  return (
    <Form id={COMPOSE_FORM_ID}>
      {/* aria-live polite so screen readers announce a restored draft when it
          appears mid-session, without blocking the user. */}
      <Box aria-live="polite">
        {showDraftRestoredBanner && (
          <Box px={2.5} pt={2}>
            <Alert
              severity="info"
              action={
                <Box display="flex" alignItems="center" gridGap={4}>
                  <OutlineButton size="small" onClick={onDiscardDraft}>Discard</OutlineButton>
                  <IconButton size="small" aria-label="Dismiss" onClick={onDismissDraftBanner}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              }
            >
              Draft restored
            </Alert>
          </Box>
        )}
      </Box>

      <FormBody>
        {/* Title identifies the send in staff history and each recipient's log
            row. Always shown — it labels the send regardless of delivery option. */}
        <Field
          fid="cmpt"
          name="title"
          component={FormikInput}
          label="Title"
          placeholder="What is this message about?"
          disabled={isSubmitting}
        />

        <FormControl margin="dense" fullWidth>
          <FormLabel htmlFor="cmp-deliveryOption">Delivery option</FormLabel>
          <Select
            id="cmp-deliveryOption"
            inputProps={{ 'aria-label': 'Delivery option' }}
            value={values.deliveryOption}
            onChange={(e) => setFieldValue('deliveryOption', e.target.value as DeliveryOption)}
            variant="outlined"
            margin="dense"
            disabled={isSubmitting}
          >
            {DELIVERY_OPTION_ORDER.map((opt) => (
              <MenuItem key={opt} value={opt}>{DELIVERY_OPTION_LABELS[opt]}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box mt={1}>
          {loadingPageContext ? (
            <Box display="flex" alignItems="center" gridGap={8} py={2}>
              <CircularProgress size={16} />
              <FormHelperText>Loading context…</FormHelperText>
            </Box>
          ) : audienceSlot}
        </Box>

        {showEmailTemplate && (
          <ComposeEmailSection
            assessmentId={assessmentId}
            hasSessionContext={hasSessionContext}
            quillRef={quillRef}
            onQuillSelectionCapture={onQuillSelectionCapture}
            onFieldFocus={onFieldFocus}
            onInsertVariable={onInsertVariable}
            isSubmitting={isSubmitting}
            tokenReport={tokenReport}
            subjectIssues={fieldTokenMessages.subject}
            bodyIssues={fieldTokenMessages.bodyHtml}
          />
        )}

        {showSMSTemplate && (
          <ComposeSmsSection
            form={form}
            assessmentId={assessmentId}
            hasSessionContext={hasSessionContext}
            onFieldFocus={onFieldFocus}
            onInsertVariable={onInsertVariable}
            isSubmitting={isSubmitting}
            tokenReport={tokenReport}
            smsIssues={fieldTokenMessages.smsBody}
          />
        )}

        {submitError && (
          <Box mt={1}>
            <FormHelperText error>{submitError}</FormHelperText>
          </Box>
        )}

        <Box height={16} />
      </FormBody>

      <ComposePanelFooter
        deliveryOption={values.deliveryOption}
        recipientCount={recipientCount}
        messageCount={messageCount}
        unreachableCount={unreachableCount}
        recipientCountLoading={recipientCountLoading}
        isSubmitting={isSubmitting}
        validateForm={validateForm}
        setTouched={setTouched}
        onPreview={onTogglePreview}
        testSource={testSource}
        onSendTest={onSendTest}
        testRecipientsStorageKey={testRecipientsStorageKey}
      />

      <PreviewDialog open={showPreview} onClose={onTogglePreview}>
        {previewSlot}
      </PreviewDialog>
    </Form>
  );
};
